import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Droplets, 
  RefreshCw, 
  Settings, 
  CheckCircle2, 
  XCircle, 
  Wifi, 
  WifiOff,
  Plus,
  Trash2,
  Save,
  AlertCircle,
  Pencil,
  X
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ConfiguracaoAgua = () => {
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dispositivos, setDispositivos] = useState([]);
  const [configuracoes, setConfiguracoes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [formData, setFormData] = useState({
    device_id: '',
    local: '',
    descricao: ''
  });

  const locaisDisponiveis = ['Sete Rios', 'Pragal', 'Santa Apolónia', 'Évora', 'Freixo'];

  // Testar conexão com a API
  const testConnection = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/agua/test-connection`);
      const data = await response.json();
      setConnectionStatus(data);
      
      if (data.success) {
        toast.success('Conexão estabelecida com sucesso!');
      } else {
        toast.error(`Falha na conexão: ${data.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      setConnectionStatus({ success: false, error: error.message });
      toast.error('Erro ao testar conexão');
    }
    setLoading(false);
  };

  // Carregar dispositivos disponíveis
  const loadDispositivos = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/agua/dispositivos`);
      const data = await response.json();
      
      if (data.success) {
        setDispositivos(data.dispositivos || []);
        if (data.dispositivos?.length === 0) {
          toast.info('Nenhum dispositivo encontrado na plataforma MyWater');
        }
      } else {
        toast.error(`Erro ao carregar dispositivos: ${data.error}`);
      }
    } catch (error) {
      toast.error('Erro ao carregar dispositivos');
    }
    setLoading(false);
  };

  // Carregar configurações existentes
  const loadConfiguracoes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/configuracoes/dispositivos?categoria=agua`);
      const data = await response.json();
      setConfiguracoes(data || []);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  };

  // Guardar configuração (criar nova)
  const saveConfiguracao = async () => {
    if (!formData.device_id || !formData.local) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/configuracoes/dispositivos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          categoria: 'agua'
        })
      });

      if (response.ok) {
        toast.success('Configuração guardada com sucesso!');
        setDialogOpen(false);
        setFormData({ device_id: '', local: '', descricao: '' });
        loadConfiguracoes();
        loadDispositivos();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao guardar configuração');
      }
    } catch (error) {
      toast.error('Erro ao guardar configuração');
    }
  };

  // Atualizar configuração existente
  const updateConfiguracao = async (deviceId) => {
    const config = configuracoes.find(c => c.device_id === deviceId);
    if (!config) return;

    try {
      const response = await fetch(`${API_URL}/api/configuracoes/dispositivos/${deviceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          local: config.local,
          descricao: config.descricao
        })
      });

      if (response.ok) {
        toast.success('Configuração atualizada com sucesso!');
        setEditingDevice(null);
        loadConfiguracoes();
      } else {
        toast.error('Erro ao atualizar configuração');
      }
    } catch (error) {
      toast.error('Erro ao atualizar configuração');
    }
  };

  // Atualizar campo de configuração localmente
  const updateLocalConfig = (deviceId, field, value) => {
    setConfiguracoes(prev => prev.map(config => 
      config.device_id === deviceId 
        ? { ...config, [field]: value }
        : config
    ));
  };

  // Remover configuração
  const removeConfiguracao = async (deviceId) => {
    if (!window.confirm('Tem a certeza que deseja remover esta configuração?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/configuracoes/dispositivos/${deviceId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Configuração removida com sucesso!');
        loadConfiguracoes();
        loadDispositivos();
      } else {
        toast.error('Erro ao remover configuração');
      }
    } catch (error) {
      toast.error('Erro ao remover configuração');
    }
  };

  useEffect(() => {
    testConnection();
    loadConfiguracoes();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Settings className="h-8 w-8 text-[#003D7A]" />
            Configuração de Água
          </h1>
          <p className="text-gray-600 mt-1">Configure os dispositivos de medição de água</p>
        </div>
        <Button 
          onClick={() => { testConnection(); loadDispositivos(); }} 
          disabled={loading}
          variant="outline"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Status da Conexão */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            {connectionStatus?.success ? (
              <Wifi className="h-5 w-5 text-green-500" />
            ) : (
              <WifiOff className="h-5 w-5 text-red-500" />
            )}
            Estado da Conexão API MyWater
          </CardTitle>
        </CardHeader>
        <CardContent>
          {connectionStatus ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                {connectionStatus.success ? (
                  <Badge className="bg-green-100 text-green-800">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Conectado
                  </Badge>
                ) : (
                  <Badge className="bg-red-100 text-red-800">
                    <XCircle className="h-3 w-3 mr-1" />
                    Desconectado
                  </Badge>
                )}
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>URL:</strong> {connectionStatus.api_url || 'Não configurado'}</p>
                <p><strong>Utilizador:</strong> {connectionStatus.user || 'Não configurado'}</p>
                {connectionStatus.error && (
                  <p className="text-red-600"><strong>Erro:</strong> {connectionStatus.error}</p>
                )}
              </div>
            </div>
          ) : (
            <div className="text-gray-500">A verificar conexão...</div>
          )}
        </CardContent>
      </Card>

      {/* Configurações de Dispositivos */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Dispositivos Configurados</CardTitle>
            <CardDescription>
              Clique no ícone de edição para alterar a denominação de cada contador
            </CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Dispositivo
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Adicionar Dispositivo</DialogTitle>
                <DialogDescription>
                  Associe um novo dispositivo a um local de monitorização
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="device_id">ID do Dispositivo *</Label>
                  <Input
                    id="device_id"
                    placeholder="Ex: d82d400112400001"
                    value={formData.device_id}
                    onChange={(e) => setFormData({ ...formData, device_id: e.target.value.toLowerCase() })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="local">Local *</Label>
                  <Select 
                    value={formData.local} 
                    onValueChange={(value) => setFormData({ ...formData, local: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o local" />
                    </SelectTrigger>
                    <SelectContent>
                      {locaisDisponiveis.map((local) => (
                        <SelectItem key={local} value={local}>{local}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="descricao">Denominação / Descrição</Label>
                  <Input
                    id="descricao"
                    placeholder="Ex: WC Piso 0, Copa, Balneários..."
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={saveConfiguracao}>
                  <Save className="h-4 w-4 mr-2" />
                  Guardar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {configuracoes.length > 0 ? (
            <div className="space-y-3">
              {configuracoes.map((config) => (
                <div 
                  key={config.device_id}
                  className={`p-4 border rounded-lg transition-all ${editingDevice === config.device_id ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
                >
                  {editingDevice === config.device_id ? (
                    // Modo de edição
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-sm text-gray-500">{config.device_id}</span>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => updateConfiguracao(config.device_id)}
                          >
                            <Save className="h-4 w-4 mr-1" />
                            Guardar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingDevice(null);
                              loadConfiguracoes(); // Reset changes
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Local</Label>
                          <Select 
                            value={config.local} 
                            onValueChange={(value) => updateLocalConfig(config.device_id, 'local', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {locaisDisponiveis.map((local) => (
                                <SelectItem key={local} value={local}>{local}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Denominação</Label>
                          <Input
                            value={config.descricao || ''}
                            onChange={(e) => updateLocalConfig(config.device_id, 'descricao', e.target.value)}
                            placeholder="Ex: WC Piso 0, Copa..."
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Modo de visualização
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Droplets className="h-5 w-5 text-cyan-500" />
                          <span className="font-semibold text-lg">{config.descricao || config.device_id}</span>
                          <Badge className="bg-cyan-100 text-cyan-800">{config.local}</Badge>
                          {config.ativo ? (
                            <Badge className="bg-green-100 text-green-800">Ativo</Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-800">Inativo</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 font-mono">{config.device_id}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setEditingDevice(config.device_id)}
                          title="Editar denominação"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => removeConfiguracao(config.device_id)}
                          title="Remover"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Settings className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Nenhuma configuração definida</p>
              <p className="text-sm mt-1">Adicione dispositivos para começar a monitorizar</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dispositivos da API (se houver novos) */}
      {dispositivos.length > 0 && dispositivos.some(d => !d.configurado) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              Dispositivos Não Configurados
            </CardTitle>
            <CardDescription>
              Dispositivos encontrados na API que ainda não foram configurados
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dispositivos.filter(d => !d.configurado).map((device) => (
                <div 
                  key={device.device}
                  className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
                >
                  <div>
                    <p className="font-medium">{device.device}</p>
                    <p className="text-sm text-gray-500">
                      Última leitura: {device.valor_m3} m³
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      setFormData({ ...formData, device_id: device.device });
                      setDialogOpen(true);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Configurar
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ConfiguracaoAgua;
